import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ConnectionOrchestrator, ConnectionState } from '../../services/connection/connection-manager.service';
import { Subscription } from 'rxjs';

interface WorkerState {
  status: 'ACTIVE' | 'CONNECTING' | 'DISCONNECTED' | 'FAILED' | 'RECONNECTING' | 'UNKNOWN';
  healthy: boolean;
  state: string;
  type: string;
  uptime: number;
  timestamp: number;
}

interface ConnectionStats {
  state: string;
  connected: boolean;
  userEmail: string;
}

@Component({
  selector: 'app-worker-dashboard',
  templateUrl: './worker-dashboard.component.html',
  styleUrls: ['./worker-dashboard.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class WorkerDashboardComponent implements OnInit, OnDestroy {

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // Worker states
  websocketWorker: WorkerState | null = null;
  connectionStats: ConnectionStats | null = null;

  // Update interval
  private updateInterval: any;
  private startTime = Date.now();
  private connectionSubscription?: Subscription;

  constructor(
    private connectionManager: ConnectionOrchestrator
  ) {}

  ngOnInit(): void {
    // Update dashboard every 100ms for real-time feel
    this.updateInterval = setInterval(() => {
      if (this.visible) {
        this.updateDashboard();
      }
    }, 100);

    // Initial update
    this.updateDashboard();
  }

  ngOnDestroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
    }
  }

  private updateDashboard(): void {
    this.updateWebSocketWorkerState();
    this.updateConnectionStats();
  }

  private updateWebSocketWorkerState(): void {
    const status = this.connectionManager.getConnectionStatus();

    this.websocketWorker = {
      status: this.mapConnectionStateToStatus(status.state),
      healthy: status.state === ConnectionState.CONNECTED,
      state: status.state,
      type: status.type,
      uptime: Date.now() - this.startTime,
      timestamp: status.timestamp
    };
  }

  private updateConnectionStats(): void {
    const status = this.connectionManager.getConnectionStatus();
    const userEmail = this.connectionManager.getUserIdentifier();

    this.connectionStats = {
      state: status.state || 'UNKNOWN',
      connected: status.state === ConnectionState.CONNECTED,
      userEmail: userEmail || ''
    };
  }

  private mapConnectionStateToStatus(state: ConnectionState): WorkerState['status'] {
    switch (state) {
      case ConnectionState.CONNECTED:
        return 'ACTIVE';
      case ConnectionState.CONNECTING:
        return 'CONNECTING';
      case ConnectionState.DISCONNECTED:
        return 'DISCONNECTED';
      case ConnectionState.RECONNECTING:
        return 'RECONNECTING';
      case ConnectionState.FAILED:
        return 'FAILED';
      default:
        return 'UNKNOWN';
    }
  }

  toggleDashboard(): void {
    this.visible = !this.visible;
    this.visibleChange.emit(this.visible);
  }

  closeDashboard(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  // Utility methods for display
  getStatusIcon(status: WorkerState['status']): string {
    switch (status) {
      case 'ACTIVE': return '🟢';
      case 'CONNECTING': return '🟡';
      case 'DISCONNECTED': return '⚪';
      case 'FAILED': return '🔴';
      default: return '⚫';
    }
  }

  getStatusColor(status: WorkerState['status']): string {
    switch (status) {
      case 'ACTIVE': return 'text-green-600';
      case 'CONNECTING': return 'text-yellow-600';
      case 'DISCONNECTED': return 'text-gray-600';
      case 'FAILED': return 'text-red-600';
      default: return 'text-gray-600';
    }
  }

  formatUptime(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Test actions
  testWorkerFailover(): void {
    console.log('[Dashboard] Test: Simulating Worker death...');
    // TODO: Implement when we have Worker pool
    alert('Worker failover testing will be available after Worker pool implementation');
  }

  exportState(): void {
    const state = {
      timestamp: new Date().toISOString(),
      websocketWorker: this.websocketWorker,
      connectionStats: this.connectionStats
    };

    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `worker-state-${Date.now()}.json`;
    a.click();

    URL.revokeObjectURL(url);
    console.log('[Dashboard] State exported:', state);
  }

  refreshDashboard(): void {
    this.updateDashboard();
    console.log('[Dashboard] Dashboard refreshed');
  }
}
